import { isReadOnlyDiffStatus } from '~/constants/task-status'
import { getTaskRequest, useTask } from '~/contexts/TaskContext'
import {
  useIndexes,
  useIsInternalUploadedIndex,
  useIsLocalSyncIndex,
  useTaskStatus,
  useUser
} from '~/hooks'
import { checkSameRepo } from '~/hooks/organization/useIndexes'
import { useIsS3Task } from '~/hooks/task/useS3Task'
import { useTaskSubmitUser } from '~/hooks/task/useTaskSubmitUser'
import { CommandType, commandTypeFromJSON } from '~/protos/agents'

/**
 * ========================================
 * 🧪 Editable for Code Review Task Comment
 * ========================================
 *
 * 📦 Index Type:
 *   - Uploaded
 *   - Local-sync
 *   - Integration Repo
 *   - Internal Repo
 *
 * 📌 State Dimension:
 *   - Whether the task is created by the user
 *   - Whether the index is available
 *   - Task status (DRAFT_GENERATED is always read-only)
 *
 * --------------------------------------------------------------------------------
 * 🧑‍💻 For the task creator:
 *
 *  | Index Type         | Code Enhance | Munit Test |
 *  |--------------------|--------------|------------|
 *  | Uploaded Repo      | ✅           | ✅         |
 *  | Local-sync Repo    | ✅           | ✅         |
 *  | Integration Repo   | ✅           | ✅         |
 *  | Internal Repo      | ✅ (Comment) | ❌ CodeGen |
 *
 * --------------------------------------------------------------------------------
 * 👀 For the task viewer:
 *
 *  | Index Type         | Code Enhance | Munit Test |
 *  |--------------------|--------------|------------|
 *  | Uploaded Repo      | ✅           | ✅         |
 *  | Local-sync Repo    | ❌           | ❌         |
 *  | Integration Repo   | ✅           | ✅         |
 *  | Internal Repo      | ✅ (Comment) | ❌ CodeGen |
 *
 * --------------------------------------------------------------------------------
 * ⚙️ Based on the index availability:
 *
 *  | Index Available    | Code Enhance | Munit Test |
 *  |--------------------|--------------|------------|
 *  | Available          | ✅           | ✅         |
 *  | Unavailable        | ✅ (Internal)| ❌ Others  |
 *
 * --------------------------------------------------------------------------------
 * 📋 Based on task status:
 *
 *  | Task Status        | Editable |
 *  |--------------------|----------|
 *  | DIFF_GENERATED     | ✅       |
 *  | DRAFT_GENERATED    | ❌       | (read-only, download only)
 *
 * Internal repos are now always comment‑enabled, regardless of whether an
 * index exists, but remain ineligible for code generation features.
 */

/**
 * Reasons why code review might be read-only.
 * Each reason is a distinct condition that blocks editing.
 */
export enum ReadOnlyReason {
  /** Repository index is not available (except for internal repos) */
  INDEX_UNAVAILABLE = 'INDEX_UNAVAILABLE',
  /** Viewer accessing local-sync or S3 task (not creator) */
  VIEWER_ACCESS_RESTRICTED = 'VIEWER_ACCESS_RESTRICTED',
  /** MuleSoft to Camel migration command doesn't support editing */
  MULESOFT_TO_CAMEL_MIGRATION = 'MULESOFT_TO_CAMEL_MIGRATION',
  /** MUnit test creation command doesn't support editing */
  MUNIT_TEST_CREATION = 'MUNIT_TEST_CREATION',
  /** Draft status is view-only (download only, no approve/feedback) */
  DRAFT_STATUS = 'DRAFT_STATUS'
}

export const useCodeReviewEditable = () => {
  const isRepoIndexAvailable = useIsRepoIndexAvailable()
  const isInternalUploadedIndex = useIsInternalUploadedIndex()
  const isLocalSyncIndex = useIsLocalSyncIndex()
  const isS3Task = useIsS3Task()
  const { task } = useTask()
  const taskStatus = useTaskStatus()
  const command = commandTypeFromJSON(task?.cmd_type)
  const submitUser = useTaskSubmitUser()
  const user = useUser()
  const isSubmitUser = submitUser === user?.user_id

  // -------------------------------------------------------------------------
  // Collect all read-only reasons (each is a distinct blocking condition)
  // -------------------------------------------------------------------------
  const readOnlyReasons: ReadOnlyReason[] = []

  // Reason: Repository index unavailable (internal repos are exempt)
  const isIndexUnavailable = !isRepoIndexAvailable && !isInternalUploadedIndex
  if (isIndexUnavailable) {
    readOnlyReasons.push(ReadOnlyReason.INDEX_UNAVAILABLE)
  }

  // Reason: Viewer (non-creator) accessing restricted repo types
  if (!isSubmitUser) {
    const isIntegrationIndex = !isLocalSyncIndex && !isS3Task && !isInternalUploadedIndex
    const hasViewerAccess = isIntegrationIndex || isInternalUploadedIndex
    if (!hasViewerAccess) {
      readOnlyReasons.push(ReadOnlyReason.VIEWER_ACCESS_RESTRICTED)
    }
  }

  // Reason: MuleSoft to Camel migration doesn't support editing
  if (command === CommandType.MULESOFT_TO_CAMEL_MIGRATION) {
    readOnlyReasons.push(ReadOnlyReason.MULESOFT_TO_CAMEL_MIGRATION)
  }

  // Reason: MUnit test creation doesn't support editing
  if (command === CommandType.MUNIT_TEST_CREATION) {
    readOnlyReasons.push(ReadOnlyReason.MUNIT_TEST_CREATION)
  }

  // Reason: Draft status is view-only (no approve/feedback actions)
  if (isReadOnlyDiffStatus(taskStatus)) {
    readOnlyReasons.push(ReadOnlyReason.DRAFT_STATUS)
  }

  const isEditable = readOnlyReasons.length === 0
  return isEditable
}

export function useIsRepoIndexAvailable() {
  const indexes = useIndexes()
  const { task } = useTask()
  const request = getTaskRequest(task)

  if (request?.metadata?.codeContext?.connType === 'S3') {
    return true
  }

  // If no repository is selected, consider index as available for comment purposes
  if (!request?.metadata?.codeContext?.repoName) {
    return true
  }

  const codeContext = request?.metadata?.codeContext
  if (!codeContext) {
    return false
  }

  const index = indexes.find(index => checkSameRepo(codeContext, index))
  return index != null
}
